import React, { useState } from 'react'
import {
    Avatar,
    Box,
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import PeopleIcon from '@material-ui/icons/People'
import StarsIcon from '@material-ui/icons/Stars'
import BusinessIcon from '@material-ui/icons/Business'
import TouchAppIcon from '@material-ui/icons/TouchApp'
import LinkIcon from '@material-ui/icons/Link'

import { VisitorFilter } from '../types'
import { VISITORS } from '../websiteData'
import { useWebsiteStyles } from '../useWebsiteStyles'
import { WebsiteKpiCard } from './WebsiteKpiCard'

const useStyles = makeStyles((theme: Theme) => ({
    tableHeader: {
        backgroundColor: theme.palette.grey[50],
        '& th': {
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: theme.palette.text.secondary,
            padding: theme.spacing(1, 2),
        },
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
        },
    },
    filterGroup: {
        display: 'flex',
        gap: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: 2,
    },
    filterBtn: {
        textTransform: 'capitalize',
        fontSize: '0.75rem',
        fontWeight: 500,
        minWidth: 0,
        padding: theme.spacing(0.5, 1.25),
        borderRadius: 6,
    },
    filterActive: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    filterInactive: {
        color: theme.palette.text.secondary,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
    },
    cardInner: {
        padding: 0,
        overflow: 'hidden',
    },
    avatarIdentified: {
        backgroundColor: theme.palette.primary.main + '18',
        color: theme.palette.primary.main,
        width: 32,
        height: 32,
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    avatarAnonymous: {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
        width: 32,
        height: 32,
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    mono: {
        fontFamily: 'monospace',
        fontSize: '0.6875rem',
    },
}))

export const VisitorTracking: React.FC = () => {
    const classes = useStyles()
    const sharedClasses = useWebsiteStyles()
    const [filter, setFilter] = useState<VisitorFilter>('all')

    const list = VISITORS.filter((v) =>
        filter === 'all'
            ? true
            : filter === 'identified'
            ? v.identified
            : !v.identified,
    )
    const identified = VISITORS.filter((v) => v.identified).length

    return (
        <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <WebsiteKpiCard
                        icon={PeopleIcon}
                        label="Visitors today"
                        value="47"
                        trend="+12%"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <WebsiteKpiCard
                        icon={StarsIcon}
                        label="Identified"
                        value={`${identified} of ${VISITORS.length}`}
                        trend={`${Math.round(
                            (identified / VISITORS.length) * 100,
                        )}%`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <WebsiteKpiCard
                        icon={BusinessIcon}
                        label="UVA students"
                        value="34"
                        trend="+8"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <WebsiteKpiCard
                        icon={TouchAppIcon}
                        label="Avg pages / visit"
                        value="4.2"
                        trend="+0.3"
                    />
                </Grid>
            </Grid>

            <Box className={`${sharedClasses.card} ${classes.cardInner}`}>
                <Box className={classes.cardHeader}>
                    <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Live visitors
                        </Typography>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ fontSize: '0.6875rem' }}
                        >
                            We de-anonymize visitors by matching IP + device
                            fingerprint to our student &amp; parent database.
                            Powered by EZOS Resolve.
                        </Typography>
                    </Box>
                    <Box className={classes.filterGroup}>
                        {(['all', 'identified', 'anonymous'] as const).map(
                            (f) => (
                                <Button
                                    key={f}
                                    size="small"
                                    className={`${classes.filterBtn} ${
                                        filter === f
                                            ? classes.filterActive
                                            : classes.filterInactive
                                    }`}
                                    onClick={() => setFilter(f)}
                                >
                                    {f}
                                </Button>
                            ),
                        )}
                    </Box>
                </Box>

                <Table size="small">
                    <TableHead className={classes.tableHeader}>
                        <TableRow>
                            <TableCell>Visitor</TableCell>
                            <TableCell>Affiliation</TableCell>
                            <TableCell>Last page</TableCell>
                            <TableCell>Visits</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>When</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.map((v) => (
                            <TableRow key={v.id} className={classes.tableRow}>
                                <TableCell>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 8 }}
                                    >
                                        <Avatar
                                            className={
                                                v.identified
                                                    ? classes.avatarIdentified
                                                    : classes.avatarAnonymous
                                            }
                                        >
                                            {v.identified
                                                ? v.name
                                                      .split(' ')
                                                      .map((p) => p[0])
                                                      .join('')
                                                      .slice(0, 2)
                                                : '?'}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {v.name}
                                            </Typography>
                                            {v.title && (
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    style={{
                                                        fontSize: '0.6875rem',
                                                    }}
                                                >
                                                    {v.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {v.company}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <span className={classes.mono}>
                                        {v.page}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {v.visits}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {v.location}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {v.time}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {v.identified ? (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            style={{ gap: 6 }}
                                        >
                                            {v.linkedin && (
                                                <IconButton
                                                    size="small"
                                                    title="LinkedIn"
                                                    style={{
                                                        border: `1px solid #e0e0e0`,
                                                        padding: 6,
                                                    }}
                                                >
                                                    <LinkIcon
                                                        style={{ fontSize: 12 }}
                                                    />
                                                </IconButton>
                                            )}
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                style={{
                                                    fontSize: '0.6875rem',
                                                    fontWeight: 600,
                                                    padding: '4px 10px',
                                                }}
                                            >
                                                Add to pipeline
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{ fontSize: '0.6875rem' }}
                                        >
                                            Tracking…
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    )
}
